import { RefObject, useEffect, useRef, useState } from 'react'

import { useMultipleRef } from '@/common/util/ref'
import { useWindowSizeChanged } from '@/common/util/window'

export const getRect = (ref: RefObject<HTMLElement | null>): DOMRect | null => {
  return ref && ref.current ? ref.current.getBoundingClientRect() : null
}

export const useRect = (reference?: RefObject<HTMLElement | null>): [ref: RefObject<any>, rect: DOMRect | null] => {
  const containerRef = useRef<HTMLElement>(null)
  const ref = reference ?? containerRef
  const [rect, setRect] = useState<DOMRect | null>(null)

  useEffect(() => {
    const rect = getRect(ref)
    setRect(rect)

    const element = ref.current
    const observer = new ResizeObserver(() => setRect(getRect(ref)))

    if (element) {
      observer.observe(element)
    }
    return () => {
      element ? observer.unobserve(element) : ''
    }
  }, [ref])

  useWindowSizeChanged(() => {
    const rect = getRect(ref)
    setRect(rect)
  }, [ref])

  return [ref, rect]
}

export const useMultipleRect = <T extends HTMLElement>(length: number): [RefObject<T>[], (DOMRect | null)[]] => {
  const refs = useMultipleRef<T>(length)
  const [rects, setRects] = useState<(DOMRect | null)[]>([])

  const updateRects = () => {
    setRects(refs.map((ref) => getRect(ref)))
  }

  useEffect(() => {
    updateRects()
  }, [refs])

  useWindowSizeChanged(() => {
    updateRects()
  }, [refs])

  return [refs, rects]
}

export const multipleRectTotalWidth = (rects: (DOMRect | null)[]): number => {
  return rects.reduce((width, rect) => {
    return width + (rect ? rect.width : 0)
  }, 0)
}

export const sumAllParentScrollLeftTop = (ref: RefObject<HTMLElement | null>): { left: number; top: number } => {
  let accumulatedScrollTop = 0
  let accumulatedScrollLeft = 0
  let scrollParent: HTMLElement | null = ref.current?.parentElement ?? null

  while (scrollParent) {
    accumulatedScrollTop += scrollParent.scrollTop || 0
    accumulatedScrollLeft += scrollParent.scrollLeft || 0
    scrollParent = scrollParent.parentElement
  }

  return { top: accumulatedScrollTop, left: accumulatedScrollLeft }
}
